import React from "react";
import styled from "styled-components";
import {
  FaEnvelope,
  FaLinkedin,
  FaInstagram,
  FaGithub,
  FaMapMarkerAlt,
  FaApple,
} from "react-icons/fa";
import logo from "../assets/images/logo-high.png";

/* ─── styled ─── */

const FooterSection = styled.footer`
  background: #0a0c1a;
  color: #fff;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const FooterInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 56px 40px 0;

  @media (max-width: 768px) {
    padding: 40px 20px 0;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1.2fr;
  gap: 48px;
  padding-bottom: 48px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 36px;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

/* ── column: brand ── */

const BrandCol = styled.div`
  img {
    height: 4em;
    margin-bottom: 16px;
    opacity: 0.9;
  }
`;

const BrandDesc = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.7;
  margin-bottom: 20px;
  max-width: 280px;
`;

const AppleCredit = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 16px 10px 14px;
  border-radius: 10px;
  margin-bottom: 20px;
  max-width: 280px;

  svg {
    color: #ffffff;
    font-size: 22px;
    flex-shrink: 0;
  }

  .credit-eyebrow {
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
    display: block;
    line-height: 1;
    margin-bottom: 3px;
  }

  .credit-text {
    font-size: 12px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
  }
`;

const SocialsRow = styled.div`
  display: flex;
  gap: 10px;
`;

const SocialIcon = styled.a`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 200, 255, 0.1);
    border-color: rgba(0, 200, 255, 0.3);
    color: #00c8ff;
    transform: translateY(-2px);
  }
`;

/* ── column: links ── */

const ColTitle = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 20px;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NavItem = styled.li``;

const NavButton = styled.button`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;

  &:hover {
    color: #00c8ff;
  }
`;

/* ── column: contact ── */

const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;

  svg {
    color: #00c8ff;
    font-size: 14px;
    margin-top: 3px;
    flex-shrink: 0;
  }

  p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.4);
    line-height: 1.5;
  }

  a {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.4);
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover {
      color: #00c8ff;
    }
  }
`;

/* ── bottom bar ── */

const BottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.25);

  span {
    color: rgba(0, 200, 255, 0.6);
  }
`;

const MadeWith = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
`;

/* ─── component ─── */

export const Footer = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <FooterSection>
      <FooterInner>
        <FooterGrid>
          {/* brand */}
          <BrandCol>
            <img src={logo} alt="Monaco IT Services" />
            <BrandDesc>
              Software studio fundado en Chile. Transformamos ideas en productos
              digitales de alto impacto para startups y empresas.
            </BrandDesc>
            <AppleCredit>
              <FaApple />
              <div>
                <span className="credit-eyebrow">Apple Recognized</span>
                <span className="credit-text">
                  Swift Student Challenge Winner — WWDC ’24
                </span>
              </div>
            </AppleCredit>
            <SocialsRow>
              <SocialIcon
                href="https://www.linkedin.com/in/martin-seguel/"
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
              >
                <FaLinkedin />
              </SocialIcon>
              <SocialIcon
                href="https://www.instagram.com/martinseguel__/"
                target="_blank"
                rel="noopener noreferrer"
                title="Instagram"
              >
                <FaInstagram />
              </SocialIcon>
              <SocialIcon
                href="https://github.com/martseguel"
                target="_blank"
                rel="noopener noreferrer"
                title="GitHub"
              >
                <FaGithub />
              </SocialIcon>
            </SocialsRow>
          </BrandCol>

          {/* nav */}
          <div>
            <ColTitle>Navegación</ColTitle>
            <NavList>
              <NavItem>
                <NavButton onClick={() => scrollTo("hero")}>Inicio</NavButton>
              </NavItem>
              <NavItem>
                <NavButton onClick={() => scrollTo("services")}>Servicios</NavButton>
              </NavItem>
              <NavItem>
                <NavButton onClick={() => scrollTo("portfolio")}>Proyectos</NavButton>
              </NavItem>
              <NavItem>
                <NavButton onClick={() => scrollTo("press")}>Prensa</NavButton>
              </NavItem>
            </NavList>
          </div>

          {/* company */}
          <div>
            <ColTitle>Empresa</ColTitle>
            <NavList>
              <NavItem>
                <NavButton onClick={() => scrollTo("about")}>Nosotros</NavButton>
              </NavItem>
              <NavItem>
                <NavButton onClick={() => scrollTo("contact")}>Contacto</NavButton>
              </NavItem>
            </NavList>
          </div>

          {/* contact */}
          <div>
            <ColTitle>Contacto</ColTitle>
            <ContactRow>
              <FaEnvelope />
              <a href="mailto:mseguel@monaco-it-services.cl">
                mseguel@monaco-it-services.cl
              </a>
            </ContactRow>
            <ContactRow>
              <FaMapMarkerAlt />
              <p>Santiago, Chile</p>
            </ContactRow>
          </div>
        </FooterGrid>

        <BottomBar>
          <Copyright>
            © {new Date().getFullYear()} <span>Monaco IT Services</span>. Todos
            los derechos reservados.
          </Copyright>
          <MadeWith>Hecho con dedicación en Chile.</MadeWith>
        </BottomBar>
      </FooterInner>
    </FooterSection>
  );
};
